/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth }   from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { usersAPI, postsAPI } from '../utils/api';
import { getAvatarUrl, formatRelativeTime, formatCount } from '../utils/helpers';
import {
  MessageCircle, Users, Globe, ArrowRight, Brain,
  Shield, Lock, Cloud, Phone, FileText, Image,
  TrendingUp, Star, Zap, Plus, ChevronRight,
  Mic, Search, Compass
} from 'lucide-react';
import './HomePage.css';

const QUICK_ACTIONS = [
  { icon:MessageCircle, label:'New Message',   color:'#6C63FF', bg:'rgba(108,99,255,.15)', path:'/chat'      },
  { icon:Users,         label:'New Group',     color:'#00D68F', bg:'rgba(0,214,143,.12)',  path:'/groups'    },
  { icon:Phone,         label:'Start Call',    color:'#00B4D8', bg:'rgba(0,180,216,.12)',  path:'/calls'     },
  { icon:Brain,         label:'Ask AI',        color:'#FF4D8D', bg:'rgba(255,77,141,.12)', path:'/ai'        },
  { icon:FileText,      label:'Upload Doc',    color:'#FFB830', bg:'rgba(255,184,48,.12)', path:'/documents' },
  { icon:Image,         label:'Upload Photo',  color:'#8B5CF6', bg:'rgba(139,92,246,.12)', path:'/media'     },
];

const FEATURES = [
  { icon:Lock,    color:'#00D68F', label:'End-to-End Encrypted' },
  { icon:Shield,  color:'#6C63FF', label:'Private & Secure'     },
  { icon:Cloud,   color:'#00B4D8', label:'Unlimited Storage'    },
  { icon:Users,   color:'#FF4D8D', label:'Groups up to 1,000'   },
];

export default function HomePage() {
  const { user }     = useAuth();
  const { isOnline } = useSocket();
  const navigate     = useNavigate();

  const [contacts, setContacts] = useState([]);
  const [posts,    setPosts]    = useState([]);
  const [loading,  setLoading]  = useState(true);

  const hour     = new Date().getHours();
  const greeting = hour<5?'Good night':hour<12?'Good morning':hour<17?'Good afternoon':hour<21?'Good evening':'Good night';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contactsRes, postsRes] = await Promise.all([
          usersAPI.getContacts().catch(() => ({ data: [] })),
          postsAPI.getFeed(1).catch(() => ({ data: [] })),
        ]);
        setContacts(Array.isArray(contactsRes.data) ? contactsRes.data.slice(0, 8) : []);
        setPosts(Array.isArray(postsRes.data) ? postsRes.data.slice(0, 6) : []);
      } catch (err) {
        setContacts([]);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const onlineContacts = contacts.filter(c => c && isOnline(c._id || c.id));

  if (loading) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', flex:1, color:'var(--text-400)' }}>
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="home-page">
      <div className="home-scroll">

        {/* Welcome Hero */}
        <div className="home-hero">
          <div className="home-hero-bg"/>
          <div className="home-hero-orb1"/>
          <div className="home-hero-orb2"/>
          <div className="home-hero-content">
            <div className="home-greeting-badge">
              <span style={{color:'var(--accent-yellow)'}}>✦</span>
              {greeting}
            </div>
            <h1 className="home-name">{user?.displayName?.split(' ')[0] || 'Welcome'}</h1>
            <p className="home-subtitle">
              {onlineContacts.length > 0
                ? `${onlineContacts.length} friend${onlineContacts.length>1?'s are':' is'} online right now`
                : 'Your messages, feed & community — all here'}
            </p>
            <div className="home-hero-actions">
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/chat')}>
                <MessageCircle size={15}/> Start a Chat
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/ai')}>
                <Brain size={15}/> Ask AI
              </button>
            </div>
          </div>
          <div className="home-hero-right">
            <div className="home-hero-avatar-wrap">
              <img src={getAvatarUrl(user)} alt="" className="home-hero-avatar"/>
              <span className="home-hero-online-dot"/>
            </div>
            <div className="home-hero-stats">
              <div className="home-hero-stat">
                <span className="home-hero-stat-val">{contacts.length}</span>
                <span className="home-hero-stat-lbl">Contacts</span>
              </div>
              <div className="home-hero-stat-divider"/>
              <div className="home-hero-stat">
                <span className="home-hero-stat-val">{onlineContacts.length}</span>
                <span className="home-hero-stat-lbl">Online</span>
              </div>
              <div className="home-hero-stat-divider"/>
              <div className="home-hero-stat">
                <span className="home-hero-stat-val">{posts.length}</span>
                <span className="home-hero-stat-lbl">Posts</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="home-section-header" style={{marginTop:8}}>
          <h2><Zap size={16} style={{color:'var(--primary-light)'}}/> Quick Actions</h2>
        </div>
        <div className="quick-actions-grid">
          {QUICK_ACTIONS.map((a,i) => {
            const Icon = a.icon;
            return (
              <button key={a.path} className="quick-action-card"
                style={{ animationDelay:`${i*0.06}s` }}
                onClick={() => navigate(a.path)}>
                <div className="quick-action-icon" style={{ background:a.bg, color:a.color }}>
                  <Icon size={22}/>
                </div>
                <span className="quick-action-label">{a.label}</span>
                <ArrowRight size={14} className="quick-action-arrow"/>
              </button>
            );
          })}
        </div>

        {/* Empty state or content */}
        {contacts.length === 0 && posts.length === 0 ? (
          <div style={{
            background:'var(--bg-700)', border:'1px solid var(--border-1)',
            borderRadius:'16px', padding:'40px', textAlign:'center', marginTop:'20px'
          }}>
            <MessageCircle size={48} style={{opacity:0.3, marginBottom:16}}/>
            <h2 style={{fontSize:'1.1rem', fontWeight:700, marginBottom:8}}>Welcome to Nexa!</h2>
            <p style={{color:'var(--text-400)', fontSize:'.9rem', marginBottom:20, maxWidth:400, margin:'0 auto 20px'}}>
              Start by searching for friends or joining communities. Your contacts and feed will appear here.
            </p>
            <div style={{display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap'}}>
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/chat')}>
                <Search size={14}/> Find People
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/explore')}>
                <Compass size={14}/> Explore
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Online Friends */}
            {onlineContacts.length > 0 && (
              <div style={{marginTop:20}}>
                <div className="home-section-header">
                  <h2><span style={{color:'var(--green)',fontSize:'.6rem'}}>●</span> Online Now</h2>
                  <button className="btn-ghost btn-sm" onClick={() => navigate('/chat')}>
                    See all <ChevronRight size={13}/>
                  </button>
                </div>
                <div className="online-friends-row">
                  {onlineContacts.slice(0,8).map(c => (
                    <button key={c._id||c.id} className="online-friend-card"
                      onClick={() => navigate(`/chat/${c._id||c.id}`)}>
                      <div className="online-friend-avatar-wrap">
                        <img src={getAvatarUrl(c)} alt="" className="online-friend-avatar"/>
                        <span className="online-friend-dot"/>
                      </div>
                      <span className="online-friend-name">{c.displayName?.split(' ')[0]}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Stats row */}
            <div className="home-stats-row" style={{marginTop:20}}>
              {[
                { label:'Total Contacts', value:contacts.length,            icon:Users,      color:'#6C63FF', sub:'People you know'    },
                { label:'Online Now',     value:onlineContacts.length,      icon:Globe,      color:'#00D68F', sub:'Currently active'   },
                { label:'Recent Posts',   value:posts.length,               icon:TrendingUp, color:'#00B4D8', sub:'From your network'  },
                { label:'Security',       value:'E2E',                      icon:Shield,     color:'#FF4D8D', sub:'Encrypted always'   },
              ].map(s => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="home-stat-card">
                    <div className="home-stat-icon" style={{background:s.color+'20',color:s.color}}>
                      <Icon size={20}/>
                    </div>
                    <div>
                      <p className="home-stat-value">{s.value}</p>
                      <p className="home-stat-label">{s.label}</p>
                      <p style={{fontSize:'.68rem',color:'var(--text-500)',marginTop:1}}>{s.sub}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Feature grid */}
            <div className="home-section-header" style={{marginTop:24}}>
              <h2><Star size={16} style={{color:'var(--accent-yellow)'}}/> Everything you need</h2>
            </div>
            <div className="features-grid">
              {[
                { icon:MessageCircle, color:'#6C63FF', label:'Encrypted Chats',     desc:'Private messages with AES-256',          path:'/chat'       },
                { icon:Phone,         color:'#00B4D8', label:'Voice & Video Calls', desc:'HD calls, end-to-end encrypted',          path:'/calls'      },
                { icon:Star,          color:'#FFB830', label:'Stories',             desc:'Share moments that last 24 hours',         path:'/stories'    },
                { icon:Brain,         color:'#FF4D8D', label:'AI Assistant',        desc:'Powered by Gemini, always ready',          path:'/ai'         },
                { icon:Users,         color:'#00D68F', label:'Groups & Communities',desc:'Up to 1,000 members per group',            path:'/communities' },
                { icon:Globe,         color:'#00B4D8', label:'Public Feed',         desc:'Global news and trending topics',          path:'/feed'       },
                { icon:FileText,      color:'#FFB830', label:'Documents',           desc:'Upload, share & preview any document',    path:'/documents'  },
                { icon:Image,         color:'#8B5CF6', label:'Media Gallery',       desc:'Photos and videos in one place',           path:'/media'      },
                { icon:Lock,          color:'#10B981', label:'Private & Secure',    desc:'Zero knowledge, your data is yours',       path:'/settings'   },
              ].map((f,i) => {
                const Icon = f.icon;
                return (
                  <div key={f.label} className="feature-card"
                    style={{transitionDelay:`${i*0.04}s`}}
                    onClick={() => navigate(f.path)}>
                    <div className="feature-card-icon" style={{background:f.color+'20',color:f.color}}>
                      <Icon size={20}/>
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <h4>{f.label}</h4>
                      <p>{f.desc}</p>
                    </div>
                    <ArrowRight size={13} className="feature-card-arrow"/>
                  </div>
                );
              })}
            </div>

            {/* AI promo */}
            <div className="ai-promo-banner" onClick={() => navigate('/ai')} style={{marginTop:24}}>
              <div className="ai-promo-glow"/>
              <div className="ai-promo-left">
                <div className="ai-promo-icon"><Brain size={26}/></div>
                <div>
                  <h3>Nexa AI Assistant</h3>
                  <p>Summarize chats, translate messages, generate replies, answer questions — powered by Gemini</p>
                </div>
              </div>
              <div className="btn btn-primary btn-sm" style={{flexShrink:0}}>
                Try AI <ArrowRight size={14}/>
              </div>
            </div>
          </>
        )}

        {/* Security badges */}
        <div className="security-badges" style={{marginTop:24, marginBottom:24}}>
          {FEATURES.map(b => { const Icon = b.icon; return (
            <div key={b.label} className="security-badge">
              <Icon size={13} style={{color:b.color}}/>{b.label}
            </div>
          );})}
        </div>

      </div>
    </div>
  );
}